import { useCallback, useEffect, useRef, useState } from 'react';

import { COOKIE_KEYS } from '@moniebook-web/types';
import {
    getItem,
    isRefreshTokenExpired,
    removeAll,
    updateAccessToken,
} from '@moniebook-web/utils';

import { useSessionTokenPayload } from '@/features/ghost-session/context/session-token-payload-context';

import { isTokenAlmostExpiring, isTokenExpired } from '../utils';

const TOKEN_CHECK_INTERVAL = 1000 * 60 * 2; // 2mins

export const useSessionManager = () => {
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const intervalId = useRef<NodeJS.Timeout | null>(null);
    const { isGhostSession } = useSessionTokenPayload();

    const checkToken = useCallback(async () => {
        const token = getItem(COOKIE_KEYS.TOKEN_VARIABLE);

        if (!token || isSessionExpired || document.hidden || isGhostSession) {
            return;
        }

        if (isTokenExpired() && isRefreshTokenExpired()) {
            setIsSessionExpired(true);
            removeAll();
            return;
        }

        if (isTokenAlmostExpiring()) {
            const data = await updateAccessToken();

            if (!data) {
                // Unable to refresh token, end the session
                setIsSessionExpired(true);
                removeAll();
            }
        }
    }, [isSessionExpired, isGhostSession]);

    const watchSession = useCallback(() => {
        if (intervalId.current) {
            return;
        }

        intervalId.current = setInterval(checkToken, TOKEN_CHECK_INTERVAL);
    }, [checkToken]);

    const handleTabVisibilityChange = useCallback(async () => {
        if (intervalId.current) {
            clearInterval(intervalId.current);
            intervalId.current = null;
        }

        if (!document.hidden) {
            await checkToken();
            watchSession();
        }
    }, [checkToken, watchSession]);

    useEffect(() => {
        watchSession();
        document.addEventListener(
            'visibilitychange',
            handleTabVisibilityChange,
        );

        return () =>
            document.removeEventListener(
                'visibilitychange',
                handleTabVisibilityChange,
            );
    }, [watchSession, handleTabVisibilityChange]);

    return { isSessionExpired };
};
